import { useCallback } from 'react';

import { useLoginOrSignupPageData, useLoginOrSignupPageState } from 'src/app/pages/loginOrSignup/pageContext';
import {
	useCanSubmitForm,
	useLoginOrSignupState,
	useLoginOrSignupTracking,
} from 'src/app/pages/loginOrSignup/shared/hooks';

import { useSendResetPasswordLink } from './useSendResetPasswordLink';

type StepData = { password: string };
type StepDataErrors = Record<`${keyof StepData}Error`, string | null>;

type Result = ModelHookResult<
	StepData & StepDataErrors & { showOtpLink: boolean },
	{
		setStepData: (data: Partial<StepData>) => void;
		submitStep: () => Promise<void>;
		trackStepView: () => void;
		sendResetPasswordLink: () => void;
		gotoOtpStep: () => void;
	}
>;

export function useLoginStep(): Result {
	const { gotoNextStep } = useLoginOrSignupState();
	const [{ password, formErrors }, { setFormData, gotoStep }] = useLoginOrSignupPageState();
	const { trackLoginPasswordSubmitView, trackResetPassword, trackPasswordSendOtpCodeClick } =
		useLoginOrSignupTracking();
	const {
		data: { experiments },
	} = useLoginOrSignupPageData();
	const sendResetPasswordLink = useSendResetPasswordLink();

	const canSubmit = useCanSubmitForm();

	const submitStep = useCallback(async () => {
		if (!canSubmit) return;
		await gotoNextStep();
	}, [gotoNextStep, canSubmit]);

	const gotoOtpStep = useCallback(() => {
		trackPasswordSendOtpCodeClick();
		gotoStep('otp', { canGoBack: true });
	}, [trackPasswordSendOtpCodeClick, gotoStep]);

	return {
		data: {
			password,
			passwordError: formErrors?.password ?? null,
			showOtpLink: experiments.corgiOtp.variation === 'v2',
		},
		actions: {
			setStepData: setFormData,
			submitStep,
			trackStepView: trackLoginPasswordSubmitView,
			sendResetPasswordLink: useCallback(() => {
				void sendResetPasswordLink();
				trackResetPassword();
			}, [trackResetPassword, sendResetPasswordLink]),
			gotoOtpStep,
		},
	};
}
